import React, { useState, useEffect, useRef } from 'react';
import {
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ActionBarPrimitive,
  useThread
} from "@assistant-ui/react";
import { MarkdownText } from './MarkdownText';
import { ImageContent } from './ImageContent';
import { ReasoningPreview } from './ReasoningPreview';
import { globalChatState } from '../../contexts/ChatStateProvider';
import { useAuth } from '../../contexts/AuthContext';

const UserActionBar = () => {
  return (
    <ActionBarPrimitive.Root 
      hideWhenRunning
      className="opacity-0 group-hover:opacity-100 transition-opacity"
    >
      <ActionBarPrimitive.Copy className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <MessagePrimitive.If copied={false}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
            <path d="m4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
          </svg>
        </MessagePrimitive.If>
        <MessagePrimitive.If copied>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20,6 9,17 4,12"/>
          </svg>
        </MessagePrimitive.If>
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
};

const AssistantActionBar = () => {
  return (
    <ActionBarPrimitive.Root 
      hideWhenRunning
      className="opacity-0 group-hover:opacity-100 transition-opacity"
    >
      <ActionBarPrimitive.Copy className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <MessagePrimitive.If copied={false}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
            <path d="m4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
          </svg>
        </MessagePrimitive.If>
        <MessagePrimitive.If copied>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20,6 9,17 4,12"/>
          </svg>
        </MessagePrimitive.If>
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
};

const UserMessage = () => {
  const { user } = useAuth();
  const userAvatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  return (
    <MessagePrimitive.Root className="group flex gap-3 mb-6">
      <div className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden">
        {userAvatar ? (
          <img 
            src={userAvatar} 
            alt="User avatar" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
            {user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}
      </div>
      <div className="flex-1 pt-1">
        <div className="text-gray-900 dark:text-white leading-relaxed">
          <MessagePrimitive.Content />
        </div>
        <UserActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantMessage = () => {
  return (
    <MessagePrimitive.Root className="group flex gap-3 mb-6">
      <div className="w-8 h-8 flex-shrink-0 bg-gray-800 rounded-full flex items-center justify-center text-white text-sm font-medium">
        P
      </div>
      <div className="flex-1 pt-1">
        <div className="text-gray-900 dark:text-white leading-relaxed">
          <MessagePrimitive.Content 
            components={{ 
              Text: MarkdownText,
              Image: ImageContent,
              Reasoning: ReasoningPreview,
            }} 
          />
        </div>
        <MessagePrimitive.If hasContent>
          <AssistantActionBar />
        </MessagePrimitive.If>
      </div>
    </MessagePrimitive.Root>
  );
};

const ExportButton = () => {
  const thread = useThread();
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      const state = globalChatState.getSnapshot();
      const sessionId = state.currentSessionId;

      if (!sessionId) {
        console.error('No active session to export');
        return;
      }

      const apiKey = localStorage.getItem('apiKey');
      const authToken = localStorage.getItem('accessToken');

      const response = await fetch(`https://api.plexe.ai/sessions/${sessionId}/export`, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
          'auth': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const data = await response.json();

      if (data.export_url) {
        window.open(data.export_url, '_blank');
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export session. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (thread.messages.length === 0) return null;

  return (
    <button
      onClick={handleExport}
      disabled={exporting}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title="Export chat to HTML"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      {exporting ? 'Exporting...' : 'Export Analysis'}
    </button>
  );
};

const ClearChatButton = () => {
  const thread = useThread();

  const handleClearChat = async () => {
    await globalChatState.clearMessages();
  };

  if (thread.messages.length === 0) return null;

  return (
    <button
      onClick={handleClearChat}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
      title="New chat session"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 4v16m8-8H4"/>
      </svg>
      New Chat
    </button>
  );
};

const PlexeSpinner = () => {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-gray-200 dark:border-slate-700 border-t-blue-600 dark:border-t-silver-400 rounded-full animate-spin"></div>
      </div>
      <div className="mt-6 text-center">
        <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">Plexe is on it</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">Setting up your AI model session...</p>
      </div>
    </div>
  );
};

const ChatInterface = ({ expandedMode = false, checkpointContext = null }) => {
  const viewportRef = useRef(null);
  const thread = useThread();
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [blockingBuildId, setBlockingBuildId] = useState(null);
  const [inputLocked, setInputLocked] = useState(false);
  const [checkpointContextSent, setCheckpointContextSent] = useState(false);
  const [autoMode, setAutoMode] = useState(false); // Default: auto mode OFF (pauses for review)
  const inputRef = useRef(null);
  const checkpointContextRef = useRef(null);

  // Handle auto mode toggle
  const handleAutoModeToggle = async () => {
    const newMode = !autoMode;
    setAutoMode(newMode);

    // Get current session ID
    const snapshot = globalChatState.getSnapshot();
    const sessionId = snapshot.currentSessionId;

    if (!sessionId) {
      console.warn('No active session - auto mode setting will apply when session starts');
      return;
    }

    try {
      const { chatService } = await import('../../services/api');
      await chatService.setAutoMode(sessionId, newMode);
      console.log(`Auto mode ${newMode ? 'enabled' : 'disabled'} for session ${sessionId}`);
    } catch (error) {
      console.error('Failed to set auto mode:', error);
      // Revert on error
      setAutoMode(!newMode);
    }
  };

  // Subscribe to session state for input locking
  useEffect(() => {
    const updateInputState = () => {
      const snapshot = globalChatState.getSnapshot();
      const sessionState = snapshot.sessionState || {};

      // Input is locked when build is active AND no question is pending
      const shouldLock = sessionState.buildActive && !sessionState.pendingQuestionId;
      setInputLocked(shouldLock);

      // Legacy: also track blockingBuildId for cancel modal
      setBlockingBuildId(snapshot.blockingBuildId);
    };

    updateInputState();
    return globalChatState.subscribe(updateInputState);
  }, []);

  useEffect(() => {
    const scrollToBottom = () => {
      if (viewportRef.current) {
        viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
      }
    };

    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [thread.messages]);

  // Handle transition from welcome screen to chat
  useEffect(() => {
    if (thread.messages.length === 1 && !thread.messages[0].content[0]?.text) {
      // User just sent first message, show spinner
      setShowSpinner(true);
      const timer = setTimeout(() => {
        setShowSpinner(false);
      }, 2000); // Show spinner for 2 seconds
      return () => clearTimeout(timer);
    }
  }, [thread.messages]);

  // Send initial checkpoint context message
  useEffect(() => {
    // Use ref to prevent duplicate sends even across re-renders
    if (checkpointContext && checkpointContext !== checkpointContextRef.current) {
      checkpointContextRef.current = checkpointContext;
      console.log('=== New checkpoint context detected, will send ===');
      console.log('Context:', checkpointContext);
    } else if (checkpointContextSent) {
      return; // Already sent, don't send again
    }

    if (checkpointContext && !checkpointContextSent && checkpointContext === checkpointContextRef.current) {
      const message = `I need help with a model build. The experiment ID is ${checkpointContext.experimentId} and it has a checkpoint at ${checkpointContext.phaseName} (${checkpointContext.phase}). Can you load this checkpoint?`;

      console.log('=== Will send checkpoint context message ===');
      console.log('Message:', message);

      // Ensure session exists first, then send
      const initAndSend = async () => {
        try {
          console.log('Initializing session...');
          // Create or get session
          const sessionId = await globalChatState.getOrCreateSession();
          console.log('Session initialized:', sessionId);

          // Wait a bit for composer to be ready
          setTimeout(() => {
            try {
              console.log('Triggering checkpoint message via input...');
              // Find the input element and set its value
              const input = document.querySelector('textarea[placeholder*="anything"]');
              if (input) {
                // Set value and trigger events to simulate user input
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                  window.HTMLTextAreaElement.prototype,
                  'value'
                ).set;
                nativeInputValueSetter.call(input, message);

                // Trigger input event
                const inputEvent = new Event('input', { bubbles: true });
                input.dispatchEvent(inputEvent);

                // Wait a bit then find and click send button
                setTimeout(() => {
                  // Try multiple selectors
                  const sendButton =
                    document.querySelector('button[aria-label="Send"]') ||
                    document.querySelector('button[type="submit"]') ||
                    document.querySelector('.composer-send-button') ||
                    document.querySelector('svg[class*="arrow"]')?.closest('button');

                  console.log('Found send button:', sendButton);

                  if (sendButton) {
                    sendButton.click();
                    setCheckpointContextSent(true);
                    console.log('=== Checkpoint context message sent via button click ===');
                  } else {
                    console.error('Send button not found, trying form submit...');
                    // Try triggering form submit
                    const form = document.querySelector('form');
                    if (form) {
                      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                      setCheckpointContextSent(true);
                    }
                  }
                }, 200);
              } else {
                console.error('Input field not found');
              }
            } catch (error) {
              console.error('Failed to send message:', error);
            }
          }, 1500);
        } catch (error) {
          console.error('Failed to initialize session:', error);
        }
      };

      initAndSend();
    } else {
      console.log('Skipping checkpoint message:', {
        noContext: !checkpointContext,
        alreadySent: checkpointContextSent
      });
    }
  }, [checkpointContext, checkpointContextSent]);

  return (
    <div 
      className="h-full w-full flex flex-col"
      style={{ backgroundColor: 'rgb(var(--app-background))' }}
    >
      <ThreadPrimitive.Root className="flex flex-col flex-1 min-h-0 w-full">
        <div className="flex-1 overflow-y-auto min-h-[750px] chat-scrollbar" ref={viewportRef}>
          <ThreadPrimitive.Viewport className={`h-full p-6 ${expandedMode ? 'max-w-3xl mx-auto' : 'max-w-4xl mx-auto'}`}>
            <ThreadPrimitive.Empty>
              {showSpinner ? (
                <PlexeSpinner />
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-blue-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-8">
                    <svg className="w-8 h-8 text-blue-600 dark:text-silver-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.091zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423L16.5 15.75l.394 1.183a2.25 2.25 0 001.423 1.423L19.5 18.75l-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">
                    How can I help you today?
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mb-12 text-center">
                    Ask me anything about your data, models, or analytics
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl w-full">
                    <ThreadPrimitive.Suggestion
                      prompt="Help me create a machine learning model for predicting customer behavior"
                      className="p-6 text-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600 dark:text-silver-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">Create a ML model</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Help me create a machine learning model for predicting customer behavior</p>
                        </div>
                      </div>
                    </ThreadPrimitive.Suggestion>
                    <ThreadPrimitive.Suggestion
                      prompt="I want to analyze my sales data and find patterns"
                      className="p-6 text-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600 dark:text-silver-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">Analyze my data</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">I want to analyze my sales data and find patterns</p>
                        </div>
                      </div>
                    </ThreadPrimitive.Suggestion>
                    <ThreadPrimitive.Suggestion
                      prompt="Create a dashboard to visualize my business metrics"
                      className="p-6 text-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600 dark:text-silver-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">Build a dashboard</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Create a dashboard to visualize my business metrics</p>
                        </div>
                      </div>
                    </ThreadPrimitive.Suggestion>
                    <ThreadPrimitive.Suggestion
                      prompt="Help me clean and enhance my customer database"
                      className="p-6 text-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600 dark:text-silver-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">Enhance my dataset</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Help me clean and enhance my customer database</p>
                        </div>
                      </div>
                    </ThreadPrimitive.Suggestion>
                  </div>
                </div>
              )}
            </ThreadPrimitive.Empty>
            
            <div className="pb-6">
              <ThreadPrimitive.Messages
                components={{
                  UserMessage: UserMessage,
                  AssistantMessage: AssistantMessage,
                }}
              />
            </div>
          </ThreadPrimitive.Viewport>
        </div>
        
        <div
          className="w-full sticky bottom-0 z-10"
          style={{ backgroundColor: 'rgb(var(--app-background))' }}
        >
          <div className={`w-full pt-4 pb-8 px-6 ${expandedMode ? 'max-w-3xl mx-auto' : 'max-w-4xl mx-auto'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Auto Mode Toggle */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-gray-700 rounded-md">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoMode}
                      onChange={handleAutoModeToggle}
                      className="w-3.5 h-3.5 text-gray-600 border-gray-300 rounded focus:ring-2 focus:ring-gray-400"
                    />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Auto Mode
                    </span>
                  </label>
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {autoMode ? 'Complete without pausing' : 'Pause for review'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ExportButton />
                <ClearChatButton />
              </div>
            </div>
            
            <ComposerPrimitive.Root className="w-full">
              <div className="w-full relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl transition-all duration-200 focus-within:ring-2 focus-within:ring-slate-300 dark:focus-within:ring-slate-600 focus-within:border-transparent">
                {inputLocked && (
                  <div className="absolute -top-12 left-0 right-0 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-200">
                    Model build in progress. You may not send new messages until the build has completed.
                  </div>
                )}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1">
                    <ComposerPrimitive.Input
                      autoFocus
                      disabled={inputLocked}
                      placeholder={inputLocked ? "Model build in progress..." : "Ask Plexe anything about models or datasets..."}
                      minRows={1}
                      className="w-full resize-none border-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent text-sm leading-relaxed disabled:cursor-not-allowed"
                      onClick={() => blockingBuildId && setShowCancelModal(true)}
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <ThreadPrimitive.If running={false}>
                      <ComposerPrimitive.Send
                        disabled={inputLocked}
                        className="w-7 h-7 bg-gray-900 text-white rounded-full hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
                        </svg>
                      </ComposerPrimitive.Send>
                    </ThreadPrimitive.If>
                    <ThreadPrimitive.If running>
                      <ComposerPrimitive.Cancel className="w-7 h-7 bg-gray-300 text-gray-600 rounded-full hover:bg-gray-400 transition-all duration-200 flex items-center justify-center">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </ComposerPrimitive.Cancel>
                    </ThreadPrimitive.If>
                  </div>
                </div>
              </div>
            </ComposerPrimitive.Root>

            {/* Cancel Build Modal */}
            {showCancelModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCancelModal(false)}>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md mx-4 shadow-xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Cancel model build?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    If you want to ask a question, you can wait until the build is completed. Or you can start a new chat for another topic.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      No
                    </button>
                    <button
                      onClick={() => {
                        globalChatState.setBlockingBuild(null);
                        setShowCancelModal(false);
                      }}
                      className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Yes, cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ThreadPrimitive.Root>
    </div>
  );
};

export default ChatInterface;